import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const INPUT =
  'w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition bg-white'
const LABEL = 'block text-xs font-medium text-gray-600 mb-1'

const EMPTY_FORM = { title: '', description: '', url: '', category: 'register' }

export default function HelpVideosTab({ showToast }) {
  const { t } = useTranslation()
  const { canEdit } = useAuth()
  const editable = canEdit()

  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [editingId, setEditingId] = useState(null)
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null)

  const categoryLabel = (cat) => ({
    register: t('help.categoryRegister'),
    payment: t('help.categoryPayment'),
    general: t('help.categoryGeneral'),
  }[cat] ?? cat)

  async function load() {
    const { data, error } = await supabase
      .from('help_videos')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true })
    if (error) showToast(error.message, 'error')
    setVideos(data ?? [])
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function resetForm() {
    setForm(EMPTY_FORM)
    setEditingId(null)
  }

  function startEdit(video) {
    setEditingId(video.id)
    setForm({
      title: video.title ?? '',
      description: video.description ?? '',
      url: video.url ?? '',
      category: video.category ?? 'general',
    })
  }

  async function save() {
    const title = form.title.trim()
    const url = form.url.trim()
    if (!title || !url) return
    setSaving(true)

    const payload = {
      title,
      description: form.description.trim() || null,
      url,
      category: form.category,
    }

    let error
    if (editingId) {
      ({ error } = await supabase.from('help_videos').update(payload).eq('id', editingId))
    } else {
      const maxOrder = videos.reduce((m, v) => Math.max(m, v.sort_order ?? 0), 0)
      ;({ error } = await supabase.from('help_videos').insert({ ...payload, sort_order: maxOrder + 1 }))
    }

    setSaving(false)
    if (error) { showToast(error.message, 'error'); return }
    showToast(editingId ? t('admin.helpToastUpdated') : t('admin.helpToastAdded'), 'success')
    resetForm()
    load()
  }

  async function toggleActive(video) {
    const { error } = await supabase
      .from('help_videos')
      .update({ is_active: !video.is_active })
      .eq('id', video.id)
    if (error) { showToast(error.message, 'error'); return }
    load()
  }

  async function remove(id) {
    const { error } = await supabase.from('help_videos').delete().eq('id', id)
    setConfirmingDeleteId(null)
    if (error) { showToast(error.message, 'error'); return }
    showToast(t('admin.helpToastDeleted'), 'success')
    load()
  }

  async function move(index, dir) {
    const target = index + dir
    if (target < 0 || target >= videos.length) return
    const arr = [...videos]
    ;[arr[index], arr[target]] = [arr[target], arr[index]]
    setVideos(arr)
    const { error } = await supabase.from('help_videos').upsert(
      arr.map((v, i) => ({
        id: v.id, title: v.title, url: v.url, category: v.category, sort_order: i,
      })),
    )
    if (error) showToast(error.message, 'error')
    load()
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-oasis-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{t('admin.helpVideosTitle')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('admin.helpVideosDesc')}</p>
      </div>

      {/* Add / edit form */}
      {editable && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700">
            {editingId ? t('admin.helpEditTitle') : t('admin.helpAddTitle')}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={LABEL}>{t('admin.helpFieldTitle')}</label>
              <input
                type="text"
                maxLength={150}
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>{t('admin.helpFieldCategory')}</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className={INPUT}
              >
                <option value="register">{categoryLabel('register')}</option>
                <option value="payment">{categoryLabel('payment')}</option>
                <option value="general">{categoryLabel('general')}</option>
              </select>
            </div>
          </div>
          <div>
            <label className={LABEL}>{t('admin.helpFieldUrl')}</label>
            <input
              type="url"
              placeholder="https://youtube.com/watch?v=…"
              value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              className={INPUT}
            />
            <p className="mt-1 text-xs text-gray-400">{t('admin.helpFieldUrlHint')}</p>
          </div>
          <div>
            <label className={LABEL}>{t('admin.helpFieldDescription')}</label>
            <textarea
              rows={2}
              maxLength={500}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className={INPUT}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving || !form.title.trim() || !form.url.trim()}
              className="rounded-lg bg-oasis-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {saving ? t('admin.helpSavingBtn') : editingId ? t('admin.helpSaveBtn') : t('admin.helpAddBtn')}
            </button>
            {editingId && (
              <button
                onClick={resetForm}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
              >
                {t('admin.helpCancelBtn')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* List */}
      {videos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-sm text-gray-400">
          {t('admin.helpVideosEmpty')}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {videos.map((video, index) => (
            <div key={video.id} className="flex items-center gap-3 px-4 py-3">
              {editable && (
                <div className="flex flex-col">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30 leading-none"
                    aria-label={t('admin.helpMoveUp')}
                  >▲</button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === videos.length - 1}
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30 leading-none"
                    aria-label={t('admin.helpMoveDown')}
                  >▼</button>
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${video.is_active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {video.title}
                  </span>
                  <span className="shrink-0 rounded-full bg-oasis-100 px-2 py-0.5 text-[11px] font-medium text-oasis-700">
                    {categoryLabel(video.category)}
                  </span>
                  {!video.is_active && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                      {t('admin.helpInactive')}
                    </span>
                  )}
                </div>
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-xs text-gray-400 hover:text-oasis-600"
                >
                  {video.url}
                </a>
              </div>

              {editable && (
                <div className="flex items-center gap-2 shrink-0">
                  {confirmingDeleteId === video.id ? (
                    <>
                      <button
                        onClick={() => remove(video.id)}
                        className="text-xs font-medium rounded-lg bg-red-600 px-3 py-1.5 text-white hover:bg-red-700 transition"
                      >
                        {t('admin.helpConfirmDeleteBtn')}
                      </button>
                      <button
                        onClick={() => setConfirmingDeleteId(null)}
                        className="text-xs font-medium rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 transition"
                      >
                        {t('admin.helpCancelBtn')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleActive(video)}
                        className="text-xs font-medium rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 transition"
                      >
                        {video.is_active ? t('admin.helpHideBtn') : t('admin.helpShowBtn')}
                      </button>
                      <button
                        onClick={() => startEdit(video)}
                        className="text-xs font-medium rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 transition"
                      >
                        {t('admin.editBtn')}
                      </button>
                      <button
                        onClick={() => setConfirmingDeleteId(video.id)}
                        className="text-xs font-medium rounded-lg border border-red-300 px-3 py-1.5 text-red-600 hover:bg-red-50 transition"
                      >
                        {t('admin.deleteBtn')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
